import React, { useEffect, useRef, useMemo } from 'react';

interface WaveformVizProps {
  /** Array of flight time values to visualize */
  data: number[];
  /** Width of the SVG */
  width?: number;
  /** Height of the SVG */
  height?: number;
  /** Stroke color */
  color?: string;
  /** Gradient fill color (with opacity) */
  fillColor?: string;
  /** Whether to animate scroll */
  animate?: boolean;
  /** Maximum number of points to display */
  maxPoints?: number;
  /** Show grid lines */
  showGrid?: boolean;
  /** Label for the visualization */
  label?: string;
}

/**
 * WaveformViz — SVG polyline visualization of flight times,
 * with gradient fill and smooth scrolling at 60fps.
 */
export default function WaveformViz({
  data,
  width = 600,
  height = 120,
  color = '#7c5cfc',
  fillColor,
  animate = true,
  maxPoints = 30,
  showGrid = true,
  label,
}: WaveformVizProps) {
  const svgRef = useRef<SVGSVGElement>(null);
  const animationRef = useRef<number | null>(null);
  const offsetRef = useRef(0);

  // Normalize data to fit within the SVG height
  const displayData = useMemo(() => {
    const d = data.slice(-maxPoints);
    if (d.length === 0) return [];

    const max = Math.max(...d, 1);
    const min = Math.min(...d, 0);
    const range = max - min || 1;
    const padding = height * 0.1;
    const usableHeight = height - padding * 2;

    return d.map((v) => padding + usableHeight * (1 - (v - min) / range));
  }, [data, maxPoints, height]);

  // Generate smooth path using Catmull-Rom spline
  const pathData = useMemo(() => {
    if (displayData.length < 2) return '';

    const step = width / (maxPoints - 1);
    const points = displayData.map((y, i) => ({ x: i * step, y }));

    // Catmull-Rom to Bezier conversion
    let d = `M ${points[0].x},${points[0].y}`;

    for (let i = 0; i < points.length - 1; i++) {
      const p0 = points[Math.max(0, i - 1)];
      const p1 = points[i];
      const p2 = points[i + 1];
      const p3 = points[Math.min(points.length - 1, i + 2)];

      const cp1x = p1.x + (p2.x - p0.x) / 6;
      const cp1y = p1.y + (p2.y - p0.y) / 6;
      const cp2x = p2.x - (p3.x - p1.x) / 6;
      const cp2y = p2.y - (p3.y - p1.y) / 6;

      d += ` C ${cp1x},${cp1y} ${cp2x},${cp2y} ${p2.x},${p2.y}`;
    }

    return d;
  }, [displayData, width, maxPoints]);

  // Fill path (closed to bottom)
  const fillPath = useMemo(() => {
    if (!pathData) return '';
    const step = width / (maxPoints - 1);
    const lastX = (displayData.length - 1) * step;
    return `${pathData} L ${lastX},${height} L 0,${height} Z`;
  }, [pathData, width, height, displayData.length, maxPoints]);

  // Grid lines
  const gridLines = useMemo(() => {
    if (!showGrid) return [];
    const lines = [];
    // Horizontal grid lines
    for (let i = 0; i < 4; i++) {
      const y = (height / 4) * (i + 0.5);
      lines.push({ x1: 0, y1: y, x2: width, y2: y });
    }
    return lines;
  }, [showGrid, height, width]);

  // Scroll animation
  useEffect(() => {
    if (!animate || displayData.length < 2) return;

    const step = width / (maxPoints - 1);
    let lastTime = performance.now();
    const speed = step / 500; // pixels per ms

    const tick = (now: number) => {
      const dt = now - lastTime;
      lastTime = now;
      offsetRef.current = (offsetRef.current + speed * dt) % step;

      if (svgRef.current) {
        const g = svgRef.current.querySelector('.waveform-content');
        if (g) {
          g.setAttribute('transform', `translate(${-offsetRef.current}, 0)`);
        }
      }
      animationRef.current = requestAnimationFrame(tick);
    };

    animationRef.current = requestAnimationFrame(tick);
    return () => {
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
    };
  }, [animate, displayData.length, width, maxPoints]);

  const gradientId = `waveform-gradient-${React.useId()}`;

  return (
    <div className="relative">
      {label && (
        <span className="block text-xs text-txt-muted font-sans mb-1">{label}</span>
      )}
      <svg
        ref={svgRef}
        width={width}
        height={height}
        viewBox={`0 0 ${width} ${height}`}
        className="overflow-hidden rounded-lg"
        preserveAspectRatio="none"
      >
        <defs>
          <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={fillColor || color} stopOpacity="0.3" />
            <stop offset="100%" stopColor={fillColor || color} stopOpacity="0.02" />
          </linearGradient>
        </defs>

        {/* Background */}
        <rect width={width} height={height} fill="#0f3460" opacity="0.3" rx="8" />

        {/* Grid */}
        {gridLines.map((line, i) => (
          <line
            key={i}
            x1={line.x1}
            y1={line.y1}
            x2={line.x2}
            y2={line.y2}
            stroke="#646681"
            strokeWidth="0.5"
            strokeDasharray="4 4"
            opacity="0.3"
          />
        ))}

        {/* Waveform content group (translated for scroll animation) */}
        <g className="waveform-content">
          {/* Gradient fill */}
          {fillPath && (
            <path
              d={fillPath}
              fill={`url(#${gradientId})`}
              className="transition-all duration-150"
            />
          )}

          {/* Stroke line */}
          {pathData && (
            <path
              d={pathData}
              fill="none"
              stroke={color}
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="transition-all duration-150"
            />
          )}

          {/* Data points */}
          {displayData.map((y, i) => {
            const step = width / (maxPoints - 1);
            return (
              <circle
                key={i}
                cx={i * step}
                cy={y}
                r="2.5"
                fill={color}
                opacity={i === displayData.length - 1 ? 1 : 0.5}
                className="transition-all duration-150"
              />
            );
          })}
        </g>

        {/* Empty state */}
        {displayData.length === 0 && (
          <text
            x={width / 2}
            y={height / 2}
            textAnchor="middle"
            fill="#646681"
            fontSize="12"
            fontFamily="Inter, sans-serif"
          >
            Comece a digitar para ver a onda
          </text>
        )}
      </svg>
    </div>
  );
}
